import { RoadmapAction } from './roadmapAction.type';
import {
  IApplication,
  IApplicationComponentRef,
  IApplicationVersionRef,
  IBaseEntity,
  PersistentResultDTO,
  ResultKey,
} from '.';

export enum SearchMode {
  GREATER_THAN = '>',
  LESS_THAN = '<',
  EQUAL = '=',
  LIKE = '~*',
}

export enum RoadmapType {
  TO_BE_COMPLIANT_WITH_PLATFORMIZATION_RULE = 'to-be-compliant-with-platformization-rule',
  TO_BE_DECOMMISSIONED = 'to-be-decommissioned',
  TO_BE_UPGRADED = 'to-be-upgraded',
  TO_BE_USED = 'to-be-used',
  WAITING_DECOMMISSIONING = 'waiting-decommissioning',

  // Special transformation path roadmap type, can only be set for Transformation path component
  FINISH_TRANSFORMATION = 'finish-transformation',

  // Special austin roadmap types, can only be set for GEN1 / GEN2 / Tradi components
  REDEPLOY = 'redeploy',
  REHOST = 'rehost',
  REPLATFORM = 'replatform',
  REFACTOR = 'refactor',

  // Special GTS roadmap
  MANAGED_BY_GTS = 'managed-by-gts',
}

export const AustinRoadmapType: Readonly<Array<RoadmapType>> = [
  RoadmapType.REDEPLOY,
  RoadmapType.REHOST,
  RoadmapType.REPLATFORM,
  RoadmapType.REFACTOR,
] as const;

export const RoadmapLabel: Record<RoadmapType, string> = {
  'to-be-decommissioned': 'To be decommissionned',
  'to-be-upgraded': 'To be upgraded',
  'to-be-compliant-with-platformization-rule': 'To be compliant with Platformization rule',
  'to-be-used': 'To be used',
  'waiting-decommissioning': 'Waiting decommissioning',
  'finish-transformation': 'Finish the transformation',
  redeploy: 'Redeploy',
  rehost: 'Rehost',
  replatform: 'Replatform',
  refactor: 'Refactor',
  'managed-by-gts': 'Managed by GTS',
};

export enum RoadmapStatus {
  ACTIVE = 'active', // Roadmap isn't outdated and hasn't reach the result goal
  OUTDATED = 'outdated', // Roadmap is outdated and hasn't reach the result goal
  RESOLVED = 'resolved', // Roadmap has reach the result goal
  PARTIALLY_RESOLVED = 'partially-resolved', // All assets link to the roadmap are resolved, but the roadmap isn't resolved
}

export enum RoadmapTransformationPath {
  AZURE_VM = 'azure-vm',
  AZURE_ORACLE = 'azure-oracle',
  AZURE_AKS = 'azure-aks',
  AZURE_PG_FLEXIBLE = 'azure-pg-flexible',
  AZURE_EVENT_HUB = 'azure-event-hub',
  AZURE_BOT = 'azure-bot',
  AZURE_CDN = 'azure-cdn',
  AZURE_COSMOS_DB = 'azure-cosmos-db',
  AZURE_REDIS_CACHE = 'azure-redis-cache',
  AZURE_FRONT_DOOR = 'azure-front-door',
  AZURE_BATCH = 'azure-batch',
  GEN2 = 'gen2',
  TRADI = 'tradi',
}

export const RoadampTransformationPathLabels: Record<
  string,
  Partial<Record<RoadmapTransformationPath, string>> | RoadmapTransformationPath
> = {
  Azure: {
    'azure-vm': 'Azure VM',
    'azure-oracle': 'Azure Oracle',
    'azure-aks': 'Azure AKS',
    'azure-pg-flexible': 'Azure PG Flexible',
    'azure-event-hub': 'Azure Event Hub',
    'azure-bot': 'Azure Bot',
    'azure-cdn': 'Azure CDN',
    'azure-cosmos-db': 'Azure Cosmos DB',
    'azure-redis-cache': 'Azure Redis Cache',
    'azure-front-door': 'Azure Front Door',
    'azure-batch': 'Azure Batch',
  },
  'GEN 2': RoadmapTransformationPath.GEN2,
  Tradi: RoadmapTransformationPath.TRADI,
};

export const RoadmapTransformationPathLabel: Record<RoadmapTransformationPath, string> = {
  'azure-vm': 'Azure VM',
  'azure-oracle': 'Azure Oracle',
  'azure-aks': 'Azure AKS',
  'azure-pg-flexible': 'Azure PG Flexible',
  'azure-event-hub': 'Azure Event Hub',
  'azure-bot': 'Azure Bot',
  'azure-cdn': 'Azure CDN',
  'azure-cosmos-db': 'Azure Cosmos DB',
  'azure-redis-cache': 'Azure Redis Cache',
  'azure-front-door': 'Azure Front Door',
  'azure-batch': 'Azure Batch',
  gen2: 'GEN 2',
  tradi: 'Tradi',
};

export const RoadampTransformationPathPickerOptions = Object.entries(RoadampTransformationPathLabels).map(
  ([label, keyOrRecord]) => ({
    key: typeof keyOrRecord === 'string' ? keyOrRecord : label,
    label,
    children:
      typeof keyOrRecord === 'string'
        ? Object.entries(RoadmapLabel)
            .filter(([key, _]) => AustinRoadmapType.includes(key as RoadmapType))
            .filter(([key, _]) =>
              keyOrRecord === RoadmapTransformationPath.TRADI
                ? [RoadmapType.REDEPLOY].includes(key as RoadmapType)
                : true
            )
            .filter(([key, _]) => key !== RoadmapType.MANAGED_BY_GTS)
            .map(([key, label]) => ({
              key: `${keyOrRecord}_${key}`,
              target: keyOrRecord,
              action: key,
              label,
            }))
        : Object.entries(keyOrRecord).map(([recordKey, recordLabel]) => ({
            key: recordKey,
            label: recordLabel,
            children: Object.entries(RoadmapLabel)
              .filter(([key, _]) => AustinRoadmapType.includes(key as RoadmapType))
              .filter(([key, _]) => key !== RoadmapType.MANAGED_BY_GTS)
              .map(([key, label]) => ({
                key: `${recordKey}_${key}`,
                label,
                target: recordKey,
                action: key,
              })),
          })),
  })
);

export const ROADMAP_STATUS: RoadmapStatus[] = [
  RoadmapStatus.ACTIVE,
  RoadmapStatus.OUTDATED,
  RoadmapStatus.RESOLVED,
  RoadmapStatus.PARTIALLY_RESOLVED,
];

export interface IRoadmap extends IBaseEntity {
  applicationId: number;
  parentRefId: number;
  scopeRefId: number;
  componentRefId: number;
  versionRefId: number | null;
  recommendationNextId: number | null;
  deletedDate: string | null;
  expectedDate: string | null;
  startDate: string | null;
  comment: string | null;
  targetedResultValue: string | null;
  targetMode: SearchMode | null;
  status: RoadmapStatus;
  actions: Array<RoadmapAction>;
  type: RoadmapType;
  transformationPath: RoadmapTransformationPath;
  austinStartDate: Date | null;
  austinEndDate: Date | null;
  enablers: Array<string> | null;
}

export interface IApplicationRoadmap extends IRoadmap {
  application: IApplication;
  componentRef: IApplicationComponentRef;
  versionRef: IApplicationVersionRef | undefined;
  persistentResults?: Array<PersistentResultDTO>;
}

export interface RoadmapView extends IRoadmap {
  applicationName: string;
  componentRefKey: string;
  componentRefName: string;
  version: string | undefined;
  persistentResults?: Array<PersistentResultDTO>;
}

export type CreateRoadmapDTO = {
  readonly applicationId: number;
  readonly componentRefId: number;
  readonly versionRefId?: number | null;
  readonly recommendationNextId: number | null;
  readonly expectedDate: string;
  readonly comment?: string | null;
  readonly targetedResultValue?: string | null;
  readonly targetedMode?: SearchMode | null;
  readonly status?: RoadmapStatus;
  readonly type: RoadmapType;
  readonly persistentResults?: Array<PersistentResultDTO>;
  readonly transformationPath?: RoadmapTransformationPath;
  readonly startDate?: string | null;
};

export type UpdateRoadmapDTO = Partial<Omit<CreateRoadmapDTO, 'applicationId' | 'componentRefId' | 'versionRefId'>>;
